import type { RaidCompositionEntryDto } from '@/api/hooks'
import { repelReading } from '@/lib/recon'

/**
 * Watchtower-gated recon read-outs for the next NPC raid, shared by the
 * DefensePanel NextRaid card and the Map incoming-attack panel. Each piece
 * (fleet composition, repel estimate) renders the revealed value when present;
 * otherwise — and only while more intel is gated behind a Watchtower upgrade — it
 * renders a "🔭 upgrade your Watchtower" nudge so the player learns the tower
 * unlocks more. When everything is revealed (full intel), the locked hints stay
 * hidden. See backend `WatchtowerIntel` / `DefenseStateOutput::nextRaid`.
 */

const SPYGLASS = '🔭'

/** A muted "upgrade the Watchtower to reveal X" line. */
export function ReconLocked({ label, compact = false }: { label: string; compact?: boolean }) {
  if (compact) {
    return (
      <span className="inline-flex items-center gap-1 text-[0.7rem] text-ocean-400 italic">
        <span aria-hidden="true">{SPYGLASS}</span> {label}
      </span>
    )
  }
  return (
    <p className="flex items-center gap-1.5 text-xs text-ocean-400 italic">
      <span aria-hidden="true">{SPYGLASS}</span> {label}
    </p>
  )
}

/**
 * « Tour de guet niveau 6 pour révéler la flotte » quand le serveur a dit quel niveau
 * il faut, « Améliore ta Tour de guet … » sinon. Le niveau vient du payload
 * (`intelTiers`, miroir de `WatchtowerIntel::tiers`) et n'est jamais recopié ici : un
 * palier rééquilibré côté serveur change la phrase sans toucher au front. Le repli
 * couvre les payloads où le champ est optionnel (la carte) ou absent.
 */
function lockedLabel(requiredLevel: number | undefined, what: string): string {
  return requiredLevel === undefined
    ? `Améliore ta Tour de guet pour ${what}`
    : `Tour de guet niveau ${requiredLevel} pour ${what}`
}

/**
 * La mention du dernier palier : l'heure exacte d'arrivée. Sans elle, un capitaine
 * qui a déjà débloqué la composition ET l'estimation de ses chances voyait bien un
 * horaire flou, mais plus AUCUNE mention verrouillée ne lui disait ce qu'il lui
 * restait à bâtir. Rendue seulement là où le compte à rebours précis a vocation à
 * s'afficher, c'est-à-dire le panneau de défense : sur la carte, l'horaire exact
 * reste volontairement hors sujet.
 */
export function ReconPreciseEta({
  precise,
  requiredLevel,
}: {
  precise: boolean
  /** Niveau de Tour exigé par ce palier (`intelTiers.preciseTiming`). */
  requiredLevel?: number
}) {
  if (precise) return null

  return <ReconLocked label={lockedLabel(requiredLevel, "connaître l'heure exacte")} />
}

/** Attacking fleet (count × type) once revealed; else a locked nudge. */
export function ReconComposition({
  composition,
  showLockedHint,
  requiredLevel,
}: {
  composition: RaidCompositionEntryDto[] | null | undefined
  showLockedHint: boolean
  /** Niveau de Tour exigé par ce palier (`intelTiers.composition`). */
  requiredLevel?: number
}) {
  if (composition && composition.length > 0) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-ocean-300 uppercase tracking-wider">Flotte</span>
        {composition.map((ship) => (
          <span key={ship.type} className="inline-flex items-center gap-1 rounded bg-ocean-900/60 px-2 py-0.5 text-sand-100 tabular-nums">
            {ship.count} × {ship.label}
          </span>
        ))}
      </div>
    )
  }
  if (showLockedHint) {
    return <ReconLocked label={lockedLabel(requiredLevel, 'révéler la flotte')} />
  }
  return null
}

/** Defender win chance as a % + a short qualitative reading; else a locked nudge. */
export function ReconRepel({
  estimate,
  showLockedHint,
  requiredLevel,
}: {
  estimate: number | null | undefined
  showLockedHint: boolean
  /** Niveau de Tour exigé par ce palier (`intelTiers.repel`). */
  requiredLevel?: number
}) {
  if (estimate !== null && estimate !== undefined) {
    const reading = repelReading(estimate)
    return (
      <p className="flex flex-wrap items-baseline gap-1.5 text-xs">
        <span className="text-ocean-300 uppercase tracking-wider">Tes chances</span>
        <span className={`font-display text-base tabular-nums ${reading.tone}`}>{reading.percent} %</span>
        <span className={`${reading.tone} opacity-90`}>· {reading.label}</span>
      </p>
    )
  }
  if (showLockedHint) {
    return <ReconLocked label={lockedLabel(requiredLevel, 'estimer tes chances')} />
  }
  return null
}
